import React, { useState, useId } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { IconCheck } from './icons';
import { useIsNarrow, gridCols } from './ui';

const API = '/api';
const isTestAccount = () => { try { return JSON.parse(localStorage.getItem('enko_auth'))?.is_test_account === true; } catch { return false; } };

const DEPARTMENTS = ['Senior Management', 'Investments', 'Operations', 'Risk', 'Compliance', 'Marketing', 'Legal', 'Finance', 'HR', 'IT'];

function ApplicationRequestForm({ currentUser, onBack }) {
  const { t } = useTranslation();
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  // RESPONSIVE Phase 2: two-column field rows reflow to one column below ~900px.
  const isNarrow = useIsNarrow();
  const rowStyle = { ...s.row, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  const blank = {
    title: '', description: '', department: '',
    problem_to_solve: '', expected_benefit: '', users_concerned: '',
    estimated_users: '', dependencies: '', systems: '',
    team_confirmed: false,
  };
  const [form, setForm] = useState(blank);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [submittedId, setSubmittedId] = useState(null);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const submit = async () => {
    if (!form.title || !form.description || !form.department || !form.problem_to_solve) {
      setError(t('appForm.errRequired'));
      return;
    }
    if (!form.team_confirmed) {
      setError(t('appForm.errTeamConfirm'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        estimated_users: form.estimated_users ? parseInt(form.estimated_users, 10) : null,
        submitted_by: currentUser,
      };
      const res = await axios.post(`${API}/application-requests/`, payload);
      const id = res.data.id;

      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`${API}/application-requests/${id}/upload`, fd);
      }
      setSubmittedId(id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error submitting request');
    }
    setSaving(false);
  };

  if (submittedId) {
    return (
      <div style={s.container}>
        <div style={s.successCard}>
          <div style={s.successIcon}><IconCheck size={48} /></div>
          <h2 style={s.successTitle}>{t('appForm.successTitle')}</h2>
          <p style={s.successText}>{t('appForm.successText')}</p>
          <button style={s.primaryBtn} onClick={onBack}>{t('appForm.backToPortal')}</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h1 style={s.title}>{t('appForm.title')}</h1>
        <p style={s.subtitle}>{t('appForm.subtitle')}</p>
      </div>

      <div style={s.card}>
        {isTestAccount() && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <button style={s.demoBtn} onClick={() => setForm({
            title: 'Automated Expense Reporting Tool',
            description: 'A tool to automate the collection, categorisation and approval of employee expense claims, replacing the current manual process.',
            department: 'Operations',
            problem_to_solve: 'Expense reports are currently submitted via email and Excel, causing delays, errors and lack of audit trail. Finance spends significant time chasing missing receipts and reconciling data.',
            expected_benefit: 'Reduce processing time by ~60%, improve auditability and free up finance bandwidth for higher-value tasks.',
            users_concerned: 'All employees (expense submission), Finance team (approval and reconciliation)',
            estimated_users: '35',
            dependencies: 'Integration with existing accounting system (QuickBooks). Requires SSO via Microsoft Azure AD.',
            systems: 'QuickBooks (accounting), Microsoft Azure AD (SSO), Microsoft 365',
            team_confirmed: true,
          })}>{t('appForm.fillDemo')}</button>
        </div>}
        {error && <div style={s.error}>{error}</div>}

        <div style={s.field}>
          <label style={s.label} htmlFor={`${a11yId}-f`}>{t('appForm.titleField')}</label>
          
          <input id={`${a11yId}-f`} style={s.input} value={form.title} onChange={set('title')}
            placeholder={t('appForm.placeholder_title')} />
        </div>

        <div style={s.field}>
          <label style={s.label} htmlFor={`${a11yId}-f-2`}>{t('appForm.department')}</label>
          
          <select id={`${a11yId}-f-2`} style={s.input} value={form.department} onChange={set('department')}>
            <option value="">{t('appForm.selectDept')}</option>
            {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div style={s.field}>
          <label style={s.label} htmlFor={`${a11yId}-f-3`}>{t('appForm.description')}</label>
          
          <textarea id={`${a11yId}-f-3`} style={s.textarea} rows={3} value={form.description} onChange={set('description')}
            placeholder={t('appForm.placeholder_description')} />
        </div>

        <div style={s.field}>
          <label style={s.label} htmlFor={`${a11yId}-f-4`}>{t('appForm.problemToSolve')}</label>
          
          <textarea id={`${a11yId}-f-4`} style={s.textarea} rows={3} value={form.problem_to_solve} onChange={set('problem_to_solve')}
            placeholder={t('appForm.placeholder_problem')} />
        </div>

        <div style={s.field}>
          <label style={s.label} htmlFor={`${a11yId}-f-5`}>{t('appForm.expectedBenefit')}</label>
          
          <textarea id={`${a11yId}-f-5`} style={s.textarea} rows={2} value={form.expected_benefit} onChange={set('expected_benefit')}
            placeholder={t('appForm.placeholder_benefit')} />
        </div>

        <div style={rowStyle}>
          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-f-6`}>{t('appForm.usersConcerned')}</label>
            
            <textarea id={`${a11yId}-f-6`} style={{ ...s.textarea, minHeight: '72px' }} value={form.users_concerned}
              onChange={set('users_concerned')} placeholder={t('appForm.placeholder_users')} />
          </div>
          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-f-7`}>{t('appForm.estimatedUsers')}</label>
            
            <input id={`${a11yId}-f-7`} style={s.input} type="number" min="1" value={form.estimated_users}
              onChange={set('estimated_users')} placeholder={t('appForm.placeholder_users_num')} />
          </div>
        </div>

        <div style={s.field}>
          <label style={s.label} htmlFor={`${a11yId}-f-8`}>{t('appForm.dependencies')}</label>
          
          <textarea id={`${a11yId}-f-8`} style={{ ...s.textarea, minHeight: '60px' }} value={form.dependencies}
            onChange={set('dependencies')}
            placeholder={t('appForm.placeholder_dependencies')} />
        </div>

        <div style={s.field}>
          <label style={s.label} htmlFor={`${a11yId}-f-9`}>{t('appForm.systems')}</label>
          
          <textarea id={`${a11yId}-f-9`} style={{ ...s.textarea, minHeight: '60px' }} value={form.systems}
            onChange={set('systems')}
            placeholder={t('appForm.placeholder_systems')} />
        </div>

        <div style={s.field}>
          <label style={s.label} htmlFor={`${a11yId}-f-10`}>{t('appForm.attachment')}</label>
          
          <input id={`${a11yId}-f-10`} type="file" onChange={e => setFile(e.target.files[0])}
            style={{ fontSize: '13px', color: '#555', marginTop: 4 }} />
          {file && <span style={s.fileHint}>{file.name}</span>}
        </div>

        <div style={s.checkRow}>
          <input type="checkbox" id="team_confirmed" checked={form.team_confirmed}
            onChange={e => setForm(f => ({ ...f, team_confirmed: e.target.checked }))}
            style={{ accentColor: '#1a1a2e', marginTop: 2 }} />
          <label htmlFor="team_confirmed" style={s.checkLabel}>
            {t('appForm.teamConfirmed')}
          </label>
        </div>

        <div style={s.btnRow}>
          <button style={s.cancelBtn} onClick={onBack}>{t('appForm.cancel')}</button>
          <button style={s.primaryBtn} onClick={submit} disabled={saving}>
            {saving ? t('appForm.submitting') : t('appForm.submitBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  container: { fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '760px', margin: '0 auto', padding: '24px', backgroundColor: '#f8f9fa' },
  backBtn: { background: 'none', border: 'none', color: '#1a1a2e', fontSize: '14px', cursor: 'pointer', padding: '0 0 16px 0', display: 'block' },
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px 0' },
  subtitle: { fontSize: '14px', color: '#888', margin: 0 },
  card: { backgroundColor: '#fff', borderRadius: '12px', padding: '28px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  error: { backgroundColor: '#fde8e8', color: '#c0392b', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginBottom: '20px' },
  field: { marginBottom: '18px' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '18px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' },
  input: { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', color: '#1a1a2e', boxSizing: 'border-box', outline: 'none' },
  textarea: { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', color: '#1a1a2e', boxSizing: 'border-box', resize: 'vertical', outline: 'none', fontFamily: 'inherit' },
  fileHint: { display: 'block', fontSize: '12px', color: '#6b7280', marginTop: '4px' },
  checkRow: { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '24px', marginTop: '8px' },
  checkLabel: { fontSize: '13px', color: '#374151', lineHeight: 1.5, cursor: 'pointer' },
  btnRow: { display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  demoBtn: { padding: '5px 12px', border: '1px solid #9ca3af', borderRadius: '5px', background: '#fff', fontSize: '12px', color: '#6b7280', cursor: 'pointer', fontWeight: '500' },
  cancelBtn: { background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', cursor: 'pointer', color: '#374151' },
  primaryBtn: { backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', cursor: 'pointer' },
  successCard: { backgroundColor: '#fff', borderRadius: '12px', padding: '48px 32px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginTop: '40px' },
  successIcon: { color: '#27ae60', marginBottom: '16px' },
  successTitle: { fontSize: '22px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 8px 0' },
  successText: { fontSize: '14px', color: '#666', margin: '0 0 24px 0', lineHeight: 1.6 },
};

export default ApplicationRequestForm;
